<template>
  <main class="grow sw">
    <!-- HERO -->
    <section class="sw-hero">
      <div class="sw-wrap">
        <h1>Move your client work from GoHighLevel to Workspace369.</h1>
        <p class="sw-lede">
          HighLevel (GoHighLevel) is built for agencies that resell marketing software: funnels, sub-accounts and SaaS mode. Its pricing page lists Starter at $97, Unlimited at $297 and Agency Pro at $497 a month, with calling and email and SMS marketing marked as usage-based. If that is your business, stay. If you run client service work and want plans with seats included and texting and calls from Voyager, the math changes. There is no one-click import, but AI Import looks for HighLevel exports: when it spots one it names HighLevel as the source, and you can pick HighLevel yourself if it doesn't. It maps your columns and shows the AI usage it needs before it writes anything. Prices below were checked on HighLevel’s own pricing page on October 23, 2026.
        </p>
        <div class="sw-cta">
          <a
            :href="addReferralToUrl('https://app.workspace369.com/signup?plan=voyager')"
            class="sw-btn"
            data-conversion-event="start_free_click"
            data-conversion-label="Switch from GoHighLevel hero CTA"
            data-conversion-location="/switch/gohighlevel"
          >Start free trial</a>
          <NuxtLink :to="localePath('/compare/gohighlevel')" class="sw-link">
            Compare Workspace369 vs GoHighLevel →
          </NuxtLink>
        </div>
        <TrialDisclosure plan="voyager" on-light />
        <ChatGptProof location="/switch/gohighlevel" />
        <p class="sw-updated">Last updated October 23, 2026</p>
      </div>
    </section>

    <!-- WHAT MOVES -->
    <section class="sw-moves">
      <div class="sw-wrap">
        <header class="sw-head">
          <p>The honest part first</p>
          <h2>What moves, what doesn't</h2>
          <span class="sw-head-copy">
            There is no one-click import from HighLevel, and Workspace369 does not claim one. You
            bring your data as spreadsheets and AI Import does the column mapping.
          </span>
        </header>

        <div class="sw-moves-grid">
          <div class="sw-move-card sw-move-yes">
            <h3>Moves with you</h3>
            <ul>
              <li>
                <strong>Clients, products, expenses, invoices and proposals.</strong> Export your contacts and records from HighLevel to CSV or XLSX and AI Import maps the columns to the right fields. You review the mapping first.
              </li>
              <li>
                <strong>The cost, before you commit.</strong> The import shows how much of your monthly usage it needs before it starts, so nothing runs on a surprise bill.
              </li>
            </ul>
          </div>

          <div class="sw-move-card sw-move-no">
            <h3>You re-create these</h3>
            <ul>
              <li>
                <strong>Files and notes.</strong> AI Import doesn't bring these. Download them from HighLevel and attach them to the client record or project by hand.
              </li>
              <li>
                <strong>Snapshots and SaaS reselling.</strong> Workspace369 has a funnel builder, so rebuild your funnels with its blocks, but it has no snapshots, white-label or SaaS reselling mode. Keep HighLevel for any you still resell.
              </li>
              <li>
                <strong>Workflows.</strong> Automations do not carry over. Rebuild the ones you actually use in Workspace369’s workflows.
              </li>
              <li>
                <strong>Your HighLevel phone numbers.</strong> Workspace369 does not promise a number port-in, so plan on picking a new number on Voyager and telling clients before you cancel HighLevel.
              </li>
              <li>
                <strong>Links and cards.</strong> Old invoice and form links keep pointing at HighLevel, and saved payment methods cannot be moved between systems.
              </li>
            </ul>
          </div>
        </div>
        <p class="sw-moves-note">
          No lock-in either way: the workspace owner can request a full data export from Workspace369 at any time. Keep your HighLevel account active until the last open invoice there is paid.
        </p>
      </div>
    </section>

    <!-- PRICE MATH -->
    <section class="sw-extra">
      <div class="sw-wrap">
        <header class="sw-head">
          <p>The price math</p>
          <h2>Per-account base price against seat plans</h2>
          <span class="sw-head-copy">
            HighLevel’s pricing page lists Starter at $97 a month (3 sub-accounts), Unlimited at $297 (unlimited sub-accounts) and Agency Pro at $497 (SaaS mode). It states unlimited users on its plans. Calling and email and SMS marketing are marked “Usage based charges apply”, and the page gives no per-unit rates. Workspace369 plans include a set number of seats and texting and calls are included from Voyager, plus a one-time $50 carrier compliance setup when you claim a number; texts and calls draw on the plan's monthly usage.
          </span>
        </header>
        <p class="sw-extra-note">Prices checked October 23, 2026 on the vendors’ own pages.</p>
        <div class="sw-table-scroll">
          <table class="sw-table sw-seat-table">
            <thead>
              <tr>
                <th scope="col">Team size</th>
                <th scope="col">HighLevel, monthly base</th>
                <th scope="col">Workspace369, monthly</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in seatMath" :key="row.size">
                <th scope="row">{{ row.size }}</th>
                <td>{{ row.them }}</td>
                <td>{{ row.w369 }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="sw-extra-note">
          HighLevel’s Starter plan ($97) is cheaper than Workspace369 from five people, because its users are unlimited. Its usage charges for calls and messages come on top of the base price, so the real monthly total depends on how much you send and call. Workspace369 includes texting and calling in the plan price from Voyager ($74), plus a one-time $50 carrier compliance setup when you claim a number; texts and calls draw on the plan's monthly usage. Source: <a href="https://www.gohighlevel.com/pricing" target="_blank" rel="noopener noreferrer">HighLevel pricing</a>, checked October 23, 2026.
        </p>
        <p class="sw-extra-note">
          What HighLevel gives you that Workspace369 does not: snapshots, sub-accounts for reselling, and SaaS mode on Agency Pro. On Workspace369, texting and calling start on Voyager ($74), and IVR, call queues, payment plans and online booking start on Specialist ($149).
        </p>
      </div>
    </section>

    <!-- CHECKLIST -->
    <section class="sw-checklist">
      <div class="sw-wrap">
        <header class="sw-head">
          <p>Step by step</p>
          <h2>The switching checklist</h2>
          <span class="sw-head-copy">Seven steps, in order.</span>
        </header>
        <ol class="sw-steps">
          <li v-for="(step, i) in steps" :key="step.title" class="sw-step">
            <span class="sw-step-num" aria-hidden="true">{{ i + 1 }}</span>
            <div>
              <h3>{{ step.title }}</h3>
              <p>{{ step.copy }}</p>
            </div>
          </li>
        </ol>
      </div>
    </section>

    <!-- STAY -->
    <section class="sw-stay">
      <div class="sw-wrap sw-wrap-narrow">
        <header class="sw-head">
          <p>Fair is fair</p>
          <h2>Stay on GoHighLevel if…</h2>
        </header>
        <div class="sw-stay-card">
          <ul>
              <li>
                <strong>You resell software to your own clients.</strong> Sub-accounts, snapshots and SaaS mode (Agency Pro, $497 a month) are the point of HighLevel. Workspace369 has no SaaS resale.
              </li>
              <li>
                <strong>You resell funnels or a white-labeled platform.</strong> Snapshots, sub-accounts and SaaS mode are HighLevel’s core, and Workspace369 has none of them.
              </li>
              <li>
                <strong>You want to white-label for clients.</strong> HighLevel’s FAQ says you can white-label the desktop web app, and its pricing page sells a white-label mobile app separately. Workspace369 does not offer white-label apps or custom domains for funnels and client pages (email can send from your own domain).
              </li>
              <li>
                <strong>You have a large team and a low base price matters.</strong> HighLevel states unlimited users on its plans, so as the team grows the base price stays at $97 to $497. Workspace369 Voyager covers 2 seats for $74, Specialist covers 10 seats for $149, and extra seats are $10 each.
              </li>
              <li>
                <strong>Your setup already runs smoothly.</strong> If your funnels and automations work and the usage bill is fine, a move has a real cost in hours and no payoff.
              </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- PLANS -->
    <section class="sw-plans" data-sticky-cta-hide>
      <div class="sw-wrap">
        <header class="sw-head">
          <p>Pick your plan</p>
          <h2>Five plans, priced by seats</h2>
          <span class="sw-head-copy">
            Cadet through Commander start with a 14-day free trial. Pay annually and each plan drops by about 20%.
            Extra seats are $10 a month each from Voyager.
          </span>
        </header>

        <div class="sw-table-scroll">
          <table class="sw-table">
            <thead>
              <tr>
                <th scope="col">Plan</th>
                <th scope="col">Monthly</th>
                <th scope="col">Seats</th>
                <th scope="col">Pick it if…</th>
                <th scope="col"><span class="sw-sr">Trial</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="plan in plans" :key="plan.name" :class="{ 'sw-row-popular': plan.popular }">
                <th scope="row">
                  {{ plan.name }}
                  <span v-if="plan.popular" class="sw-popular-tag">Recommended</span>
                </th>
                <!-- Phone cards repeat the column names; the header row is hidden there. -->
                <td><span class="sw-cell-label" aria-hidden="true">Monthly</span>{{ plan.price }}</td>
                <td><span class="sw-cell-label" aria-hidden="true">Seats</span>{{ plan.seats }}</td>
                <td>{{ plan.fit }}</td>
                <td>
                  <NuxtLink
                    v-if="plan.slug === 'enterprise'"
                    :to="localePath('/contact-sales')"
                    class="sw-row-cta"
                    data-conversion-event="demo_request_click"
                    :data-conversion-label="`Switch from GoHighLevel plan CTA: ${plan.name}`"
                    data-conversion-location="/switch/gohighlevel"
                  >Talk to sales</NuxtLink>
                  <a
                    v-else
                    :href="addReferralToUrl(`https://app.workspace369.com/signup?plan=${plan.slug}`)"
                    class="sw-row-cta"
                    :class="{ 'sw-row-cta-filled': plan.popular }"
                    data-conversion-event="start_free_click"
                    :data-conversion-label="`Switch from GoHighLevel plan CTA: ${plan.name}`"
                    data-conversion-location="/switch/gohighlevel"
                  >Try {{ plan.name }}</a>
                  <TrialDisclosure v-if="plan.slug !== 'enterprise'" compact :plan="plan.slug" on-light />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="sw-plans-note">
          Prices in USD. Full feature matrix on the
          <NuxtLink :to="localePath('/pricing')">pricing page</NuxtLink>.
        </p>
      </div>
    </section>

    <!-- FAQ -->
    <section class="sw-faq">
      <div class="sw-wrap sw-wrap-narrow">
        <header class="sw-head">
          <p>FAQ</p>
          <h2>Switching questions, answered straight</h2>
        </header>
        <div class="sw-faq-list">
          <details v-for="item in faq" :key="item.q" class="sw-faq-item">
            <summary>
              <h3>{{ item.q }}</h3>
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </summary>
            <div class="sw-faq-answer">
              <p>{{ item.a }}</p>
              <p v-if="item.linkHref" class="sw-faq-more">
                <a :href="item.linkHref" target="_blank" rel="noopener noreferrer">{{ item.linkLabel }} ↗</a>
              </p>
            </div>
          </details>
        </div>
      </div>
    </section>

    <!-- INTERNAL LINKS -->
    <section class="sw-explore">
      <div class="sw-wrap">
        <header class="sw-head sw-head-center">
          <p>Keep exploring</p>
          <h2>Before you decide</h2>
        </header>
        <div class="sw-explore-grid">
          <NuxtLink v-for="card in explore" :key="card.path" :to="localePath(card.path)" class="sw-explore-card">
            <h3>{{ card.title }}</h3>
            <p>{{ card.copy }}</p>
            <span>Read →</span>
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- FINAL CTA -->
    <section class="sw-band" data-sticky-cta-hide>
      <div class="sw-wrap">
        <h2>Ready to make the move?</h2>
        <p>
          Start the trial, run the checklist above, and cancel HighLevel when the last open invoice there is paid.
        </p>
        <div class="sw-cta sw-cta-center">
          <a
            :href="addReferralToUrl('https://app.workspace369.com/signup?plan=voyager')"
            class="sw-btn sw-btn-light"
            data-conversion-event="start_free_click"
            data-conversion-label="Switch from GoHighLevel final CTA"
            data-conversion-location="/switch/gohighlevel"
          >Start free trial</a>
          <NuxtLink :to="localePath('/compare/gohighlevel')" class="sw-link sw-link-light">
            See the full comparison →
          </NuxtLink>
        </div>
        <TrialDisclosure plan="voyager" on-dark centered />
      </div>
    </section>
    <PhoneStickyCta href="https://app.workspace369.com/signup?plan=voyager" plan="voyager" label="Switch from GoHighLevel" location="/switch/gohighlevel" watch=".sw-hero .sw-cta" />
  </main>
</template>

<script setup>
import "~/assets/css/switch-dark.css";
const { addReferralToUrl } = useReferral();
const { localePath } = useWorkspaceLocale();
const siteUrl = "https://workspace369.com";
const pageUrl = `${siteUrl}/switch/gohighlevel/`;
const pageTitle = "Switch from GoHighLevel to Workspace369 | Guide";
const metaDescription =
  "Switch from GoHighLevel if funnels and SaaS resale aren’t your business: what moves with AI Import, seats included, texts and calls from Voyager, a checklist.";

const steps = [
  {
    "title": "Export what you can from HighLevel",
    "copy": "Export contacts and open invoices into spreadsheets and download the files you need. Check HighLevel’s help center for what each export includes. Skip closed work; it stays in HighLevel as your archive."
  },
  {
    "title": "Start the trial and set up your services and prices",
    "copy": "The 14-day trial is enough time to move. Add your services, packages and rates first so proposals and invoices take minutes afterwards."
  },
  {
    "title": "Run AI Import for each record type",
    "copy": "Upload a CSV or XLSX for clients, products, expenses, invoices or proposals. AI Import maps your columns, you review the mapping, and the usage it needs is shown before you start."
  },
  {
    "title": "Re-create your workflows and forms",
    "copy": "Rebuild the automations and intake forms you actually use. Most people rebuild a short list, not everything. Funnels and snapshots do not move; rebuild funnels with Workspace369’s funnel builder."
  },
  {
    "title": "Add your team and set permissions",
    "copy": "Seats are included in the plan, with extra seats at $10 a month from Voyager. You can limit a contractor to the clients assigned to them."
  },
  {
    "title": "Set up texting, calls and Stripe",
    "copy": "Pick a new number on Voyager or above for texting and calls. Connect Stripe once and every invoice goes out with a payment link, with payment plans on Specialist and above."
  },
  {
    "title": "Cancel HighLevel when the last open invoice there is paid",
    "copy": "Keep the account active until then, and until you have your exports. Tell clients about the new number and invoice links before you cancel."
  }
];

const plans = [
  {
    name: "Cadet",
    slug: "cadet",
    price: "$29",
    seats: "1",
    fit: "You're solo and don't need texting yet. Email automations included (up to 3 active workflows).",
    popular: false,
  },
  {
    name: "Voyager",
    slug: "voyager",
    price: "$74",
    seats: "2",
    fit: "You want to text and call clients — this is the entry point for SMS, calling and voicemail, with SMS steps in up to 10 active workflows.",
    popular: false,
  },
  {
    name: "Specialist",
    slug: "specialist",
    price: "$149",
    seats: "10",
    fit: "You want IVR, call queues and call recording, payment plans, and up to 25 active workflows.",
    popular: true,
  },
  {
    name: "Commander",
    slug: "commander",
    price: "$299",
    seats: "20",
    fit: "You want unlimited workflows with AI drafting, plus full accounting.",
    popular: false,
  },
  {
    name: "Enterprise",
    slug: "enterprise",
    price: "$699",
    seats: "50",
    fit: "You run several teams and need 50 seats in one plan.",
    popular: false,
  },
];

const seatMath = [
  {
    size: "1 person",
    them: "$97 (Starter, 3 sub-accounts)",
    w369: "$29 (Cadet, 1 seat; texting and calls from Voyager, $74)"
  },
  {
    size: "2 people",
    them: "$97 (Starter, unlimited users)",
    w369: "$74 (Voyager, 2 seats, texting and calls included), plus a one-time $50 carrier compliance setup when you claim a number"
  },
  {
    size: "10 people",
    them: "$97 (Starter, unlimited users)",
    w369: "$149 (Specialist, 10 seats)"
  },
  {
    size: "20 people",
    them: "$97 (Starter, unlimited users)",
    w369: "$249 (Specialist $149 + 10 extra seats at $10)"
  },
  {
    size: "Resell software to clients",
    them: "$497 (Agency Pro, SaaS mode)",
    w369: "Not offered"
  }
];

const faq = [
  {
    "q": "What is the best GoHighLevel alternative?",
    "a": "It depends on what you use it for. If you resell software or live in funnels and snapshots, stay on GoHighLevel. If you mainly want CRM and marketing follow-up, HubSpot, Keap or ActiveCampaign may fit better than Workspace369. If client delivery and billing matter most, Workspace369 fits, from Voyager ($74/mo, 2 seats), with basic accounting from Specialist ($149/mo) and a branded client portal on every plan but no sub-accounts or white-label resale. Checked October 23, 2026; the GoHighLevel alternatives page has the full comparison."
  },
  {
    "q": "Is there a GoHighLevel importer?",
    "a": "Not a one-click one. AI Import looks for HighLevel exports: when it spots one it names HighLevel as the source, and you can pick HighLevel yourself if it doesn't. You upload a CSV or XLSX of clients, products, expenses, invoices or proposals, and before anything runs it shows how the columns map and the AI usage it needs. You review the result before it saves."
  },
  {
    "q": "Is GoHighLevel cheaper than Workspace369?",
    "a": "On base price, it depends on team size. HighLevel Starter is $97 a month and states unlimited users, so it is cheaper than Workspace369 from about five people (Voyager plus three extra seats is $104). Its calling and messaging are marked as usage-based on its pricing page, so the total depends on how much you send and call. Workspace369 Voyager is $74 for 2 seats with texting and calls included, plus a one-time $50 carrier compliance setup when you claim a number; texts and calls draw on the plan's monthly usage.",
    "linkHref": "https://www.gohighlevel.com/pricing",
    "linkLabel": "HighLevel pricing"
  },
  {
    "q": "Does the Workspace369 trial need a card?",
    "a": "Yes. You add a card to start the 14-day trial on Cadet through Commander, and nothing is charged if you cancel in Settings → Billing before it ends. Keep GoHighLevel running while you try it, and cancel GoHighLevel once your clients and open work have moved.",
  },
  {
    "q": "Does Workspace369 do funnels, snapshots or SaaS mode?",
    "a": "Funnels, yes; snapshots and SaaS mode, no. Every plan has a block funnel builder with hosted funnels (1 to 50 live funnels by plan), but there are no snapshots, reseller sub-accounts or SaaS mode. If you resell software, HighLevel is the better fit."
  },
  {
    "q": "How is usage billed on each side?",
    "a": "HighLevel’s pricing page marks inbound and outbound calling and email and SMS marketing as usage-based, and its FAQ says usage charges cover phone numbers, SMS and emails sent. It also lists rebilling of phone and email to your own clients on the Unlimited and Agency Pro plans. Workspace369 includes two-way texting and calling from Voyager ($74/month, 2 seats), plus a one-time $50 carrier compliance setup when you claim a number; texts and calls draw on the plan's monthly usage. IVR, call queues and call recording come with Specialist ($149/month).",
    "linkHref": "https://www.gohighlevel.com/pricing",
    "linkLabel": "HighLevel pricing"
  },
  {
    "q": "Can I keep my phone numbers?",
    "a": "Workspace369 does not promise a number port-in. Plan on picking a new number on Voyager and telling clients before you cancel HighLevel."
  },
  {
    "q": "What does AI Import cost?",
    "a": "It draws on your plan’s monthly AI usage, and the usage it needs is shown before you start the import, so you can decide before anything runs. Email credits are separate."
  },
  {
    "q": "Am I locked in to Workspace369?",
    "a": "No. The workspace owner can request a full export of the workspace data and can delete the workspace, with a 24-hour grace period to cancel the deletion."
  },
  {
    "q": "How long does switching take?",
    "a": "Most teams re-enter their active work in a few hours: current clients, open proposals and unpaid invoices. Rebuilding workflows and templates is usually the longest part."
  }
];

const explore = [
  {
    "title": "Workspace369 vs GoHighLevel",
    "copy": "The head-to-head, including funnels, delivery, phone and accounting.",
    "path": "/compare/gohighlevel"
  },
  {
    "title": "GoHighLevel alternatives",
    "copy": "GoHighLevel next to SuiteDash, Plutio, HoneyBook and others, with who should stay.",
    "path": "/alternatives/gohighlevel"
  },
  {
    "title": "Client portal",
    "copy": "What clients see: approvals, files, invoices and messages.",
    "path": "/features/client-portal"
  },
  {
    "title": "Inbox and communications",
    "copy": "The shared inbox, texting and the business phone line.",
    "path": "/features/communications"
  },
  {
    "title": "Pricing",
    "copy": "All five plans, the full feature matrix, and annual billing discounts.",
    "path": "/pricing"
  },
  {
    "title": "AI Import",
    "copy": "How the CSV import maps columns and shows the cost first.",
    "path": "/integrations/csv-import"
  }
];

useSeoMeta({
  title: pageTitle,
  description: metaDescription,
  ogTitle: pageTitle,
  ogDescription: metaDescription,
  ogUrl: pageUrl,
  ogType: "website",
  twitterCard: "summary_large_image",
  twitterTitle: pageTitle,
  twitterDescription: metaDescription,
});

useHead({
  link: [{ key: "canonical", rel: "canonical", href: pageUrl }],
  script: [
    {
      key: "workspace369-switch-gohighlevel-schema",
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebPage",
            "@id": `${pageUrl}#webpage`,
            name: pageTitle,
            url: pageUrl,
            description: metaDescription,
            dateModified: "2026-10-31",
            isPartOf: { "@id": `${siteUrl}/#website` },
            about: { "@id": `${siteUrl}/#software` },
          },
          {
            "@type": "BreadcrumbList",
            "@id": `${pageUrl}#breadcrumb`,
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${siteUrl}/` },
              { "@type": "ListItem", position: 2, name: "Switch from GoHighLevel", item: pageUrl },
            ],
          },
          {
            "@type": "FAQPage",
            "@id": `${pageUrl}#faq`,
            mainEntity: faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          },
        ],
      }),
    },
  ],
});
</script>
<style scoped>
.sw { background: #fff; color: #111827; }

.sw-wrap {
  max-width: 56rem;
  margin-inline: auto;
  padding-inline: 1.25rem;
}
@media (min-width: 640px) { .sw-wrap { padding-inline: 1.75rem; } }

.sw-wrap-narrow { max-width: 44rem; }

.sw-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

/* HERO */
.sw-hero {
  /* Clears the fixed header: 36px offer strip + 64px nav, both scale with text size. */
  padding: 7.5rem 0 3.5rem;
  background:
    radial-gradient(42rem 22rem at 12% -8rem, rgb(14 116 144 / 0.08), transparent 70%);
}
@media (min-width: 768px) { .sw-hero { padding: 8rem 0 4rem; } }

.sw-hero h1 {
  margin-top: 0;
  max-width: 44rem;
  font-size: clamp(2.05rem, 4.6vw, 3.3rem);
  font-weight: 900;
  letter-spacing: -0.03em;
  line-height: 1.05;
  text-wrap: balance;
}

.sw-lede {
  margin-top: 1.3rem;
  max-width: 42rem;
  color: #4b5563;
  font-size: 1.02rem;
  line-height: 1.75;
}

.sw-cta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.2rem;
  margin-top: 2rem;
}

.sw-cta-center { justify-content: center; }

.sw-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 3rem;
  border-radius: 8px;
  background: #111827;
  color: #fff;
  padding: 0.75rem 1.5rem;
  font-size: 0.94rem;
  font-weight: 800;
  box-shadow: 0 10px 24px -6px rgb(17 24 39 / 0.3);
  transition: background-color 150ms ease, transform 150ms ease;
}

.sw-btn:hover { background: #0e7490; transform: translateY(-1px); }

.sw-btn-light { background: #fff; color: #111827; }
.sw-btn-light:hover { background: #f0f9fc; }

.sw-link { color: #0e7490; font-size: 0.94rem; font-weight: 800; }
.sw-link:hover { text-decoration: underline; }
.sw-link-light { color: #67e8f9; }

.sw-updated {
  margin-top: 1.4rem;
  color: #5f6b7a;
  font-size: 0.78rem;
  font-weight: 600;
}

/* SECTION HEADS */
.sw-head { max-width: 42rem; margin-bottom: 2.25rem; }
.sw-head-center { margin-inline: auto; text-align: center; }

.sw-head p {
  color: #0e7490;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.sw-head h2 {
  margin-top: 0.55rem;
  font-size: clamp(1.7rem, 3.2vw, 2.35rem);
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.1;
  text-wrap: balance;
}

.sw-head-copy {
  display: block;
  margin-top: 0.85rem;
  max-width: 40rem;
  color: #4b5563;
  font-size: 0.96rem;
  line-height: 1.7;
}

.sw-head-center .sw-head-copy { margin-inline: auto; }

/* WHAT MOVES */
.sw-moves { padding: 1rem 0 4rem; }
@media (min-width: 768px) { .sw-moves { padding: 1.5rem 0 5rem; } }

.sw-moves-grid {
  display: grid;
  gap: 1rem;
}
@media (min-width: 820px) { .sw-moves-grid { grid-template-columns: 1fr 1fr; } }

.sw-move-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  padding: 1.5rem 1.6rem;
}

.sw-move-yes { border-top: 3px solid #0e7490; }
.sw-move-no { border-top: 3px solid #cbd5e1; }

.sw-move-card h3 { font-size: 1.02rem; font-weight: 800; }

.sw-move-card ul {
  margin-top: 0.9rem;
  display: grid;
  gap: 0.75rem;
  list-style: none;
  padding: 0;
}

.sw-move-card li {
  color: #4b5563;
  font-size: 0.9rem;
  line-height: 1.65;
  padding-left: 1.1rem;
  position: relative;
}

.sw-move-card li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.55em;
  width: 0.35rem;
  height: 0.35rem;
  border-radius: 999px;
  background: #9aa3ad;
}

.sw-move-yes li::before { background: #0e7490; }

.sw-move-card li strong { color: #111827; font-weight: 750; }

.sw-moves-note {
  margin-top: 1.5rem;
  border: 1px solid rgb(14 116 144 / 0.25);
  border-radius: 10px;
  background: #f0f9fc;
  padding: 1.05rem 1.2rem;
  color: #334155;
  font-size: 0.9rem;
  line-height: 1.7;
}

/* CHECKLIST */
.sw-checklist {
  background: #f7f8f9;
  border-block: 1px solid #eef0f3;
  padding: 3.5rem 0;
}
@media (min-width: 768px) { .sw-checklist { padding: 4.5rem 0; } }

.sw-steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.8rem;
}

.sw-step {
  display: flex;
  gap: 1.1rem;
  align-items: flex-start;
  border: 1px solid #e5e7eb;
  border-radius: 0.95rem;
  background: #fff;
  padding: 1.25rem 1.4rem;
}

.sw-step-num {
  display: grid;
  place-items: center;
  width: 2.1rem;
  height: 2.1rem;
  flex-shrink: 0;
  border-radius: 999px;
  background: #0e7490;
  color: #fff;
  font-size: 0.92rem;
  font-weight: 900;
  box-shadow: 0 0 0 4px rgb(14 116 144 / 0.12);
}

.sw-step h3 { font-size: 0.99rem; font-weight: 800; line-height: 1.35; }

.sw-step p {
  margin-top: 0.4rem;
  color: #4b5563;
  font-size: 0.89rem;
  line-height: 1.65;
}

/* PLANS */
.sw-plans { padding: 3.5rem 0; }
@media (min-width: 768px) { .sw-plans { padding: 4.5rem 0; } }

.sw-table-scroll { position: relative; overflow-x: auto; }

.sw-table {
  width: 100%;
  min-width: 44rem;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.sw-table th,
.sw-table td {
  padding: 0.95rem 1.1rem;
  text-align: left;
  font-size: 0.89rem;
  border-bottom: 1px solid #eef0f3;
  vertical-align: middle;
}

.sw-table thead th {
  background: #f7f8f9;
  color: #6b7280;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.sw-table tbody tr:last-child th,
.sw-table tbody tr:last-child td { border-bottom: none; }

.sw-table tbody th {
  color: #111827;
  font-size: 0.94rem;
  font-weight: 850;
  white-space: nowrap;
}

.sw-table tbody td { color: #4b5563; line-height: 1.5; }

.sw-table tbody td:nth-child(2) {
  color: #111827;
  font-weight: 800;
  white-space: nowrap;
}

.sw-table tbody td:nth-child(3) { white-space: nowrap; }

/* Plan table: give "Pick it if…" room so rows don't grow tall on phones. */
.sw-table:not(.sw-seat-table) tbody td:nth-child(4) { min-width: 20rem; }
.sw-table:not(.sw-seat-table) .sw-popular-tag { display: block; width: max-content; margin: 0.35rem 0 0; }

.sw-row-popular { background: #f0f9fc; }

.sw-popular-tag {
  display: inline-block;
  margin-left: 0.45rem;
  border-radius: 4px;
  background: #0e7490;
  color: #fff;
  padding: 0.14rem 0.5rem;
  font-size: 0.62rem;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  vertical-align: middle;
}

.sw-row-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #0e7490;
  border-radius: 6px;
  padding: 0.42rem 0.95rem;
  color: #0e7490;
  font-size: 0.8rem;
  font-weight: 800;
  white-space: nowrap;
  transition: background-color 150ms ease, color 150ms ease;
}

.sw-row-cta:hover { background: #0e7490; color: #fff; }

.sw-row-cta-filled { background: #0e7490; color: #fff; }
.sw-row-cta-filled:hover { background: #155e75; border-color: #155e75; }

/* The plan's own short trial line, directly under its Try button (B1.1). */
.sw-table td :deep(.trial-disclosure.compact) { margin: 0.4rem 0 0; line-height: 1.35; }

.sw-cell-label { display: none; }

/* Phones: the 44rem table hid every Try button off screen at 390px, so each plan becomes a card:
   name and price on top, seats, "Pick it if…", then a full-width Try button with its trial line. */
@media (max-width: 639px) {
  .sw-table:not(.sw-seat-table) { min-width: 0; border: 0; border-radius: 0; overflow: visible; background: none; }
  .sw-table:not(.sw-seat-table) thead {
    position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap;
  }
  .sw-table:not(.sw-seat-table) tbody { display: grid; gap: 0.75rem; }
  .sw-table:not(.sw-seat-table) tbody tr {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.45rem 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
    padding: 1rem 1.1rem 1.1rem;
  }
  .sw-table:not(.sw-seat-table) tbody tr.sw-row-popular { background: #f0f9fc; }
  .sw-table:not(.sw-seat-table) tbody :is(th, td) { display: block; padding: 0; border: 0; min-width: 0; }
  .sw-table:not(.sw-seat-table) tbody th { grid-column: 1; align-self: center; white-space: normal; }
  .sw-table:not(.sw-seat-table) tbody td:nth-child(2) {
    grid-column: 2; grid-row: 1; align-self: center; text-align: right; font-size: 1.35rem;
  }
  .sw-table:not(.sw-seat-table) tbody td:nth-child(3) { grid-column: 1 / -1; }
  .sw-table:not(.sw-seat-table) tbody td:nth-child(4) { grid-column: 1 / -1; min-width: 0; font-size: 0.9rem; }
  .sw-table:not(.sw-seat-table) tbody td:nth-child(5) { grid-column: 1 / -1; margin-top: 0.35rem; }
  .sw-table:not(.sw-seat-table) .sw-row-cta { display: flex; width: 100%; min-height: 2.75rem; font-size: 0.92rem; }
  .sw-table:not(.sw-seat-table) td :deep(.trial-disclosure.compact) { text-align: center; }
  .sw-table:not(.sw-seat-table) .sw-cell-label {
    display: block;
    color: #6b7280;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .sw-table:not(.sw-seat-table) tbody td:nth-child(3) .sw-cell-label { display: inline; margin-right: 0.4rem; }
}

.sw-plans-note {
  margin-top: 1rem;
  color: #6b7280;
  font-size: 0.82rem;
}

.sw-plans-note a { color: #0e7490; font-weight: 750; }
.sw-plans-note a:hover { text-decoration: underline; }

.sw-extra { padding: 3.5rem 0; }
@media (min-width: 768px) { .sw-extra { padding: 4.5rem 0; } }
.sw-extra-note { margin-top: 1rem; color: #4b5563; font-size: 0.86rem; line-height: 1.7; max-width: 44rem; }
.sw-extra-note a { color: #0e7490; font-weight: 750; }
.sw-extra-note a:hover { text-decoration: underline; }
.sw-seat-table { min-width: 36rem; }
/* White, not grey: the band above it (the setup checklist) is already grey, and two grey bands read as one. */
.sw-stay { background: transparent; border-block: 1px solid #eef0f3; padding: 3.5rem 0; }
@media (min-width: 768px) { .sw-stay { padding: 4.5rem 0; } }
.sw-stay-card { border: 1px solid #e5e7eb; border-top: 3px solid #cbd5e1; border-radius: 10px; background: #fff; padding: 1.5rem 1.6rem; }
.sw-stay-card ul { display: grid; gap: 0.75rem; list-style: none; padding: 0; margin: 0; }
.sw-stay-card li { color: #4b5563; font-size: 0.92rem; line-height: 1.65; padding-left: 1.1rem; position: relative; }
.sw-stay-card li::before { content: ""; position: absolute; left: 0; top: 0.55em; width: 0.35rem; height: 0.35rem; border-radius: 999px; background: #9aa3ad; }
.sw-stay-card li strong { color: #111827; font-weight: 750; }

/* VOICES */
.sw-voices {
  background: #f7f8f9;
  border-block: 1px solid #eef0f3;
  padding: 3.5rem 0;
}
@media (min-width: 768px) { .sw-voices { padding: 4.5rem 0; } }

.sw-quotes {
  display: grid;
  gap: 1rem;
}
@media (min-width: 860px) { .sw-quotes { grid-template-columns: repeat(3, 1fr); } }

.sw-quote {
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  padding: 1.5rem 1.6rem;
  margin: 0;
}

.sw-quote blockquote {
  margin: 0;
  color: #111827;
  font-size: 1.02rem;
  font-weight: 750;
  line-height: 1.5;
}

.sw-quote figcaption {
  margin-top: 1rem;
  color: #5f6b7a;
  font-size: 0.78rem;
  line-height: 1.55;
}

.sw-quote figcaption a { color: #0e7490; font-weight: 750; }
.sw-quote figcaption a:hover { text-decoration: underline; }

.sw-voices-fair {
  margin-top: 1.6rem;
  max-width: 40rem;
  color: #4b5563;
  font-size: 0.88rem;
  line-height: 1.7;
}

/* FAQ */
.sw-faq { padding: 3.5rem 0; }
@media (min-width: 768px) { .sw-faq { padding: 4.5rem 0; } }

.sw-faq-list { display: grid; gap: 0.7rem; }

.sw-faq-item {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  transition: border-color 150ms ease;
}

.sw-faq-item[open] { border-color: rgb(14 116 144 / 0.4); }

.sw-faq-item summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  cursor: pointer;
  padding: 1.05rem 1.2rem;
  list-style: none;
}

.sw-faq-item summary::-webkit-details-marker { display: none; }

.sw-faq-item summary h3 { font-size: 0.97rem; font-weight: 800; line-height: 1.35; }

.sw-faq-item summary svg {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  color: #9aa3ad;
  transition: transform 160ms ease;
}

.sw-faq-item[open] summary svg { transform: rotate(180deg); color: #0e7490; }

.sw-faq-answer {
  border-top: 1px solid #eef0f3;
  padding: 1rem 1.2rem 1.2rem;
}

.sw-faq-answer p {
  color: #4b5563;
  font-size: 0.9rem;
  line-height: 1.7;
}

.sw-faq-more { margin-top: 0.6rem; }

.sw-faq-more a { color: #0e7490; font-weight: 750; font-size: 0.86rem; }
.sw-faq-more a:hover { text-decoration: underline; }

/* EXPLORE */
.sw-explore {
  background: #f7f8f9;
  border-block: 1px solid #eef0f3;
  padding: 3.5rem 0;
}
@media (min-width: 768px) { .sw-explore { padding: 4.5rem 0; } }

.sw-explore-grid {
  display: grid;
  gap: 1rem;
}

@media (min-width: 760px) { .sw-explore-grid { grid-template-columns: 1fr 1fr; } }
@media (min-width: 1024px) { .sw-explore-grid { grid-template-columns: repeat(3, 1fr); } }

.sw-explore-card {
  display: grid;
  gap: 0.45rem;
  align-content: start;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  padding: 1.4rem 1.5rem;
  transition: border-color 150ms ease, transform 150ms ease, box-shadow 150ms ease;
}

.sw-explore-card:hover {
  border-color: rgb(14 116 144 / 0.4);
  transform: translateY(-2px);
  box-shadow: 0 14px 32px -10px rgb(15 23 42 / 0.1);
}

.sw-explore-card h3 { font-size: 0.98rem; font-weight: 800; }

.sw-explore-card p { color: #4b5563; font-size: 0.85rem; line-height: 1.6; }

.sw-explore-card span {
  margin-top: 0.35rem;
  color: #0e7490;
  font-size: 0.82rem;
  font-weight: 800;
}

/* FINAL CTA */
.sw-band {
  background:
    radial-gradient(38rem 20rem at 50% 0%, rgb(14 116 144 / 0.22), transparent 70%),
    #020617;
  color: #fff;
  padding: 4.5rem 0;
  text-align: center;
}

.sw-band h2 {
  font-size: clamp(1.8rem, 3.4vw, 2.6rem);
  font-weight: 900;
  letter-spacing: -0.02em;
  text-wrap: balance;
}

.sw-band p {
  margin: 0.9rem auto 0;
  max-width: 32rem;
  color: #cbd5e1;
  font-size: 1rem;
  line-height: 1.65;
}
/* Phones: the final band's trial line sits directly under its button (legal B1.1) and the comparison link moves
   below it instead of wrapping in between. Wider screens keep the button row. */
@media (max-width: 767px) {
  .sw-band .sw-wrap { display: flex; flex-direction: column; align-items: center; }
  .sw-band .sw-wrap > * { order: 1; }
  .sw-band .sw-cta { display: contents; }
  .sw-band .sw-cta > .sw-btn { order: 2; margin-top: 2rem; }
  /* TrialDisclosure renders a fragment, so it carries no scope id: reach it with :deep(). */
  .sw-band .sw-wrap > :deep(.trial-disclosure) { order: 3; }
  .sw-band .sw-cta > .sw-link { order: 4; margin-top: 0.9rem; }
}
</style>
